import DeleteConfirmation, { type DeleteConfirmationProps } from '@/components/DeleteConfirmation'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type RequestDeleteOptions = Pick<
	DeleteConfirmationProps,
	'title' | 'description' | 'confirmButtonText' | 'cancelButtonText' | 'iconClassName' | 'confirmButtonIconClassName'
> & {
	onConfirm: () => void | Promise<void>
}

/**
 * Standard delete confirmation flow for grids and action buttons.
 *
 * @example
 * const { requestDelete, DeleteConfirmationDialog } = useDeleteConfirmation()
 * requestDelete({
 *   title: t('...', 'Delete plan'),
 *   description: t('...', 'This cannot be undone.'),
 *   onConfirm: async () => { await api.delete(id) },
 * })
 * return (<>{DeleteConfirmationDialog} ...</>)
 */
export function useDeleteConfirmation() {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const [options, setOptions] = useState<RequestDeleteOptions | null>(null)
	const [confirming, setConfirming] = useState(false)

	const requestDelete = useCallback((opts: RequestDeleteOptions) => {
		setOptions(opts)
		setOpen(true)
	}, [])

	const close = useCallback(() => {
		if (confirming) return
		setOpen(false)
		setOptions(null)
	}, [confirming])

	const handleConfirm = useCallback(async () => {
		if (!options?.onConfirm || confirming) return
		setConfirming(true)
		try {
			await options.onConfirm()
			setOpen(false)
			setOptions(null)
		} finally {
			setConfirming(false)
		}
	}, [options, confirming])

	const DeleteConfirmationDialog = (
		<DeleteConfirmation
			isOpen={open}
			onClose={close}
			onConfirm={handleConfirm}
			confirming={confirming}
			title={options?.title ?? t('Manage.Helper.Delete_Title', 'Delete item')}
			description={options?.description ?? t('Manage.Helper.Delete_Description', 'Are you sure you want to delete this? This action cannot be undone.')}
			confirmButtonText={options?.confirmButtonText ?? t('Manage.Helper.Delete_Confirm', 'Delete')}
			cancelButtonText={options?.cancelButtonText}
			iconClassName={options?.iconClassName}
			confirmButtonIconClassName={options?.confirmButtonIconClassName}
		/>
	)

	return { requestDelete, close, confirming, DeleteConfirmationDialog }
}
